import React from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useSnippetId, useYupValidationResolver } from '~/hooks';
import { resolveFormFields, combineFields, formFieldsToGraphQl } from '~/utils';
import { fields, forms } from '~/pages/projects/meta/data/projects.schema';
import { pick } from 'lodash';

export const useProjectForm = () => {
    // form Fields
    const formFields = {
        object: resolveFormFields(forms.object, fields.project),
        project: resolveFormFields(forms.project, fields.project),
        client: resolveFormFields(forms.client, fields.project),
        insurance: resolveFormFields(forms.insurance, fields.project),
    };

    // merge multiple sections
    const form: any = [...forms.object, ...forms.project, ...forms.client, ...forms.insurance];
    const formToGql = formFieldsToGraphQl(form, fields.project);

    // Default (remote) values & Validation
    const {
        defaultValues,
        rules,
        watchFields: watchFieldsArray,
        watchFieldValues,
    } = combineFields(...Object.values(formFields));

    const { typSnippetId: subprojectCreditStandingSnippetId } = useSnippetId({
        name: 'ungeklärt',
        category: 'Subprojekt',
        property: 'Bonitaet',
    });

    // react-hook-form
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
        getValues,
        reset,
        setError,
        clearErrors,
    } = useForm({
        defaultValues,
        shouldFocusError: true,
        resolver: useYupValidationResolver(yup.object(rules)),
    });

    const watched = watch();
    const watchFields = pick(watched, ...watchFieldsArray);
    // const watchFields = watch(watchFieldsArray); // this suddenly gives back an array instead of an object

    React.useEffect(() => {
        watchFieldValues.forEach(({ name, resolveValue }: any) => {
            const { enabled, getValue } = resolveValue(watchFields);
            if (enabled) {
                !(watched?.[name] === getValue(watchFields)) &&
                    setValue(name, getValue?.(watchFields));
            }
        });
    }, [watchFields, watched]);

    React.useEffect(() => {
        if (subprojectCreditStandingSnippetId) {
            setValue('subprojectCreditStandingSnippetId', {
                value: subprojectCreditStandingSnippetId,
            });
        }
    }, [subprojectCreditStandingSnippetId]);

    return {
        formToGql,
        formFields,
        defaultValues,
        setValue,
        watch,
        watchFields,
        subprojectCreditStandingSnippetId,
        // useForm
        control,
        reset,
        register,
        handleSubmit,
        errors,
        setError,
        clearErrors,
    };
};
